import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm } from 'react-hook-form'
import { useLocation } from 'react-router-dom'
import { AuthFrame } from '@layerit/ui/components/auth-frame'
import { Alert, AlertDescription } from '@layerit/ui/components/ui/alert'
import { Button } from '@layerit/ui/components/ui/button'
import { Field, FieldError, FieldLabel } from '@layerit/ui/components/ui/field'
import { Input } from '@layerit/ui/components/ui/input'
import { followLink } from '@web/lib/navigation'
import { ROUTES, isSafeRedirectPath } from '@web/router'
import { useAuthFlow } from '@web/screens/auth/flow'
import { LegalLine, ProviderButtons } from '@web/screens/auth/pieces'
import type { EmailFormValues } from '@web/screens/auth/schema'
import { emailFormSchema } from '@web/screens/auth/schema'
import { publicSiteHref } from '@web/site'
import { APP_NAME } from '@layerit/brand'

interface SignInScreenProps {
	error?: string | null
	redirectTo?: string
	navigate: (to: string) => void
}

export function requestedDestination(search: string): string | null {
	const requested = new URLSearchParams(search).get('redirect')
	return requested !== null && isSafeRedirectPath(requested) ? requested : null
}

export function SignInScreen({ error = null, redirectTo, navigate }: SignInScreenProps) {
	const { search } = useLocation()
	const destination = redirectTo ?? requestedDestination(search) ?? ROUTES.getStarted
	const flow = useAuthFlow({ redirectTo: destination, verifyTo: ROUTES.signInVerify, navigate, error })
	const wordmarkHref = publicSiteHref('/')

	const form = useForm<EmailFormValues>({
		resolver: zodResolver(emailFormSchema),
		defaultValues: { email: '' },
	})

	async function signIn({ email }: EmailFormValues) {
		await flow.sendCode(email)
	}

	return (
		<AuthFrame
			wordmarkHref={wordmarkHref}
			corner={
				<Button
					nativeButton={false}
					render={<a className="px-4" href={ROUTES.signUp} onClick={(event) => followLink(event, ROUTES.signUp, navigate)} />}
				>
					Sign up
				</Button>
			}
			aside={
				<>
					<h2 className="font-display text-3xl text-ink lg:text-4xl">Every signature, on-brand.</h2>
					<p className="text-sm leading-relaxed text-ink-2">
						{APP_NAME} writes each person’s signature through the provider’s own API, re-reading the directory on a schedule.
					</p>

					<div className="mt-2 flex flex-col gap-3 border-t border-line pt-6">
						<p className="record text-ink-2">
							<span className="whitespace-nowrap">Google Workspace</span> ·{' '}
							<span className="whitespace-nowrap">Microsoft 365</span> ·{' '}
							<span className="whitespace-nowrap">Zoho Workplace</span>
						</p>
					</div>
				</>
			}
		>
			<header className="text-center">
				<h1 className="font-display text-2xl text-ink">Sign in to {APP_NAME}</h1>
				<p className="mx-auto mt-2 max-w-[32ch] text-sm leading-relaxed text-ink-3">
					We’ll email you a six-digit code. There is no password to remember.
				</p>
			</header>

			{flow.unavailable && (
				<p role="status" className="mt-6 rounded-md border border-warn/40 bg-warn/10 px-3 py-2 text-xs text-warn">
					{APP_NAME} could not reach the server to load all sign-in options, so only email codes are shown. Reload once the API is
					reachable if you expected a passkey or provider button.
				</p>
			)}

			<div className="mt-7">
				<ProviderButtons
					methods={flow.methods}
					pending={flow.pending}
					redirectTo={destination}
					layout="row"
					onPasskey={() => void flow.signInWithPasskey()}
				/>
			</div>

			{(flow.methods.social.length > 0 || flow.methods.passkey) && (
				<div className="mt-5 flex items-center gap-3 text-xs text-ink-3 uppercase">
					<span className="h-px flex-1 bg-line" />
					or
					<span className="h-px flex-1 bg-line" />
				</div>
			)}

			{flow.failure && (
				<Alert variant="destructive" className="mt-6">
					<AlertDescription>{flow.failure}</AlertDescription>
				</Alert>
			)}

			<form className="mt-7 flex flex-col gap-5" noValidate onSubmit={(event) => void form.handleSubmit(signIn)(event)}>
				<Controller
					control={form.control}
					name="email"
					render={({ field, fieldState }) => (
						<Field className="gap-2" data-invalid={fieldState.invalid}>
							<FieldLabel htmlFor="email">Work email</FieldLabel>
							<Input
								{...field}
								id="email"
								type="email"
								autoComplete="email"
								required
								className="h-10"
								aria-invalid={fieldState.invalid}
							/>
							{fieldState.invalid && <FieldError errors={[fieldState.error]} />}
						</Field>
					)}
				/>

				<Button type="submit" variant="primary" className="h-10" disabled={flow.pending}>
					{flow.pending ? 'Sending…' : 'Sign in'}
				</Button>
			</form>

			<p className="mt-6 text-center text-xs leading-relaxed text-ink-3">
				Don’t have an account?{' '}
				<a
					className="font-medium text-ink-2 underline underline-offset-2 hover:text-ink"
					href={ROUTES.signUp}
					onClick={(event) => followLink(event, ROUTES.signUp, navigate)}
				>
					Sign up
				</a>
			</p>

			<LegalLine lead={`By continuing, I agree to ${APP_NAME}’s`} className="mt-3 text-center text-balance" />
		</AuthFrame>
	)
}
